import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React from 'react';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {AppLifecycleManagement} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {Header3} from 'ui-library/Headers';
import EditableTextField, {TextFieldType} from 'ui-library/inputs/EditableTextField';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import InputWrapper from 'ui-library/inputs/InputWrapper';

import {WebhookConfigurationSectionWebhookConfiguration as WebhookConfigurationSectionWebhookConfigurationGQL} from './__generated/WebhookConfigurationSectionWebhookConfiguration';

type WebhookConfigurationSectionWebhookConfiguration = ExtractByTypename<
  WebhookConfigurationSectionWebhookConfigurationGQL['webhookConfigurations'][number],
  'WebhookConfigurationNode'
>;

export const WEBHOOK_CONFIGURATION_SECTION_WEBHOOK_CONFIGURATION: DocumentNode = gql`
  fragment WebhookConfigurationSectionWebhookConfiguration on BenchlingAppNode {
    id
    webhookConfigurations {
      ... on InaccessibleInfoNode {
        invalidId
      }
      ... on WebhookConfigurationNode {
        id
        endpointPublicKeySet
        webhookUrl
      }
    }
  }
`;

export {WebhookConfigurationSectionWebhookConfiguration};
export interface WebhookConfigurationSectionProps {
  onUpdateWebhookURL: (newWebhookURL: string) => void;
  webhookConfigurations: Array<WebhookConfigurationSectionWebhookConfiguration>;
  hasFeatures: boolean;
  hasMessageSubscriptions: boolean;
  lifecycleManagement: AppLifecycleManagement;
}

export default function WebhookConfigurationSection({
  onUpdateWebhookURL,
  webhookConfigurations,
  hasFeatures,
  hasMessageSubscriptions,
  lifecycleManagement,
}: WebhookConfigurationSectionProps): JSX.Element {
  assertAndContinue(
    webhookConfigurations.length <= 1,
    'Expected to render WebhookConfigurationSection only where there less than or equal to one webhookConfiguration'
  );

  const webhookURL = webhookConfigurations[0]?.webhookUrl;
  const unaddressedWebhookUrlRequirements = webhookURL
    ? []
    : ([] as Array<string>).concat(
        hasFeatures ? ['features'] : [],
        hasMessageSubscriptions ? ['message subscriptions'] : [],
        lifecycleManagement === AppLifecycleManagement.AUTOMATIC &&
          !BenchlingConfig.ENABLE_BENCHLING_APPS_REMOVED_LIFECYCLE_MANAGEMENT
          ? ['an AUTOMATIC lifecycle management']
          : []
      );

  return (
    <div>
      <Header3 className="u-spacing-top">Webhook Connection</Header3>
      <div>
        {unaddressedWebhookUrlRequirements.length > 0 && (
          <div className={css(styles.missingWebhookUrlWarning)}>
            <InlineBanner
              bannerType={InlineBannerType.WARNING}
              text="Add Webhook URL"
              supportingInfo={`Apps with ${unaddressedWebhookUrlRequirements.join(', ')} require webhooks.`}
            />
          </div>
        )}
        <InputWrapper
          id={_.uniqueId('benchlingAppDetail')}
          label="Webhook URL"
          labelStyle={FieldLabel.STYLES.LABEL}
        >
          <EditableTextField
            emptyValueAllowed={true}
            requestChange={onUpdateWebhookURL}
            type={TextFieldType.TEXT}
            value={webhookURL}
          />
        </InputWrapper>
      </div>
      To learn more about verifying webhooks, visit our{' '}
      <a
        target="_blank"
        rel="noopener noreferrer"
        href="https://docs.benchling.com/docs/webhook-verification"
      >
        webhook verification documentation.
      </a>
    </div>
  );
}

const styles = StyleSheet.create({
  missingWebhookUrlWarning: {
    marginBottom: 10,
  },
});
